import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { StaggerItem } from '@/shared/ui/animations';
import { ROUTE_PATTERNS } from '@/shared/config/routes';
import { EmptyState } from '@/shared/ui/EmptyState';

import {
  AnalysisSectionLayout,
  PROSE_CARD_STAGGER_SLOTS,
  SectionSeparator,
} from '../../components';
import {
  useAnalysisFromContext,
  type AnalysisSectionProps,
} from '../../utils/useAnalysisFromContext';
import { brandColor } from '../../utils/brandColor';
import { competitorDomains, resolveAdvertiserDomain } from '@/shared/utils/brandDomains';
import { formatMonth } from '../../utils/formatMonth';
import { orderAdvertisersBySpend } from '../../utils/orderAdvertisers';
import { BrandsAnalysis } from './components/BrandsAnalysis';
import { ExecutiveSummary } from './components/ExecutiveSummary';
import { PositioningMap } from './components/PositioningMap';
import type { CreativeLandscapeBodyProps } from './CreativeLandscape.types';
import { parseCreativeLandscapeData } from './model/creativeLandscapeData.schema';

// This page has no leading highlight card, but the offset keeps its cascade on the
// same rhythm as sections that have one.
const CARD_SETTLE_GAP = 1;
const sectionStagger = (order: number) => PROSE_CARD_STAGGER_SLOTS + CARD_SETTLE_GAP + 1 + order;

const CreativeLandscapeBody = ({
  data,
  creative,
  competitors,
  exporting,
}: CreativeLandscapeBodyProps) => {
  const { t } = useTranslation('analysis');

  const domains = useMemo(() => competitorDomains(competitors), [competitors]);
  const brands = useMemo(() => {
    // Ranked by media spend so a brand's colour matches the one it has on Media Landscape.
    const ordered = orderAdvertisersBySpend(data.availableAdvertisers, data.mediaLandscape.monthly);
    return ordered.map((name) => ({
      name,
      color: brandColor(name, ordered),
      domain: resolveAdvertiserDomain(name, domains),
    }));
  }, [data.availableAdvertisers, data.mediaLandscape.monthly, domains]);

  const range = useMemo(() => {
    const { start, end } = data.dateRange;
    if (!start || !end) return '';
    return start === end
      ? formatMonth(start, 'numeric')
      : `${formatMonth(start, 'numeric')} – ${formatMonth(end, 'numeric')}`;
  }, [data.dateRange]);

  const meta = t(
    range
      ? 'analysisResult.creativeLandscape.overviewMeta'
      : 'analysisResult.creativeLandscape.overviewMetaNoRange',
    { count: creative.analyzedAdvertisers.length, range },
  );

  const hasSummary = creative.executiveSummary.length > 0;
  const hasBrands = creative.brands.length > 0;
  const hasPositions = creative.positioning.brands.length > 0;

  return (
    <div className="flex flex-col gap-3xl pb-md">
      {hasSummary && (
        <StaggerItem index={sectionStagger(0)} variant="swipeUp">
          <ExecutiveSummary brands={brands} rows={creative.executiveSummary} meta={meta} />
        </StaggerItem>
      )}
      {hasSummary && hasBrands && <SectionSeparator />}
      {hasBrands && (
        <StaggerItem index={sectionStagger(1)} variant="swipeUp">
          <BrandsAnalysis creative={creative} brands={brands} exporting={exporting} />
        </StaggerItem>
      )}
      {hasBrands && hasPositions && <SectionSeparator />}
      {hasPositions && (
        <StaggerItem index={sectionStagger(2)} variant="swipeUp">
          <PositioningMap creative={creative} brands={brands} exporting={exporting} />
        </StaggerItem>
      )}
    </div>
  );
};

export const CreativeLandscape = ({
  analysis: analysisProp,
  exporting,
}: AnalysisSectionProps = {}) => {
  const { t } = useTranslation('analysis');
  const analysis = useAnalysisFromContext(analysisProp);
  const parsed = useMemo(
    () => parseCreativeLandscapeData(analysis.pathmaticsData),
    [analysis.pathmaticsData],
  );

  // A blob whose creative agent failed persists as `null`, and one the agent returned empty has
  // nothing to draw. Both are indistinguishable from no upload to the reader, so they get the
  // same state rather than a page of empty headings.
  const creative = parsed.status === 'ok' ? parsed.data.creativeLandscape : null;
  const hasNoData =
    parsed.status === 'absent' ||
    creative === null ||
    (creative.brands.length === 0 &&
      creative.positioning.brands.length === 0 &&
      creative.executiveSummary.length === 0);

  return (
    <AnalysisSectionLayout
      analysisId={analysis.id}
      brand={analysis.brandName}
      section={ROUTE_PATTERNS.IQ_SECTION_CREATIVE_LANDSCAPE}
      title={t('analysisResult.creativeLandscape.creativeLandscape')}
      subtitle={t('analysisResult.creativeLandscape.subtitle', { brandName: analysis.brandName })}
      exporting={exporting}
    >
      {parsed.status === 'invalid' && (
        <EmptyState
          title={t('analysisResult.creativeLandscape.unreadableData')}
          description={t('analysisResult.creativeLandscape.unreadableDataDescription')}
          height="100%"
        />
      )}
      {parsed.status !== 'invalid' && hasNoData && (
        <EmptyState
          title={t('analysisResult.creativeLandscape.noCreativeData')}
          description={t('analysisResult.creativeLandscape.noCreativeDataDescription')}
          height="100%"
        />
      )}
      {parsed.status === 'ok' && !hasNoData && (
        <CreativeLandscapeBody
          key={analysis.id}
          data={parsed.data}
          creative={creative}
          exporting={exporting}
          competitors={analysis.structuredData?.categoryCompetition?.topCompetitors}
        />
      )}
    </AnalysisSectionLayout>
  );
};
